import { useMemo, useState } from "react";
import { Search, ChevronRight, ChevronDown, Shield, Upload, Lock, X } from "lucide-react";
import { OpenAPISpec, ParsedEndpoint } from "../types";
import { cn } from "../utils/cn";

interface SidebarProps {
  spec: OpenAPISpec;
  endpoints: ParsedEndpoint[];
  selectedEndpointId: string | null;
  onSelectEndpoint: (id: string) => void;
  onSelectOverview: () => void;
  onUploadNew: () => void;
  onAuthorize: () => void;
  credentials: Record<string, string>;
  specId: string | null;
  hasSecuritySchemes: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export const METHOD_COLORS: Record<string, string> = {
  GET: "text-blue-600 bg-blue-50 border-blue-200",
  POST: "text-emerald-600 bg-emerald-50 border-emerald-200",
  PUT: "text-amber-600 bg-amber-50 border-amber-200",
  DELETE: "text-red-600 bg-red-50 border-red-200",
  PATCH: "text-purple-600 bg-purple-50 border-purple-200",
  OPTIONS: "text-slate-600 bg-slate-50 border-slate-200",
  HEAD: "text-slate-600 bg-slate-50 border-slate-200",
};

export function Sidebar({
  spec,
  endpoints,
  selectedEndpointId,
  onSelectEndpoint,
  onSelectOverview,
  onUploadNew,
  onAuthorize,
  credentials,
  specId,
  hasSecuritySchemes,
  isOpen = true,
  onClose,
}: SidebarProps) {
  const [search, setSearch] = useState("");
  const [expandedTags, setExpandedTags] = useState<Record<string, boolean>>({});

  const handleSelectEndpoint = (id: string) => {
    onSelectEndpoint(id);
    onClose?.();
  };

  const handleSelectOverview = () => {
    onSelectOverview();
    onClose?.();
  };

  const groupedEndpoints = useMemo(() => {
    const groups: Record<string, ParsedEndpoint[]> = {};
    const filtered = endpoints.filter(
      (e) =>
        e.path.toLowerCase().includes(search.toLowerCase()) ||
        e.operation.summary?.toLowerCase().includes(search.toLowerCase()),
    );

    filtered.forEach((endpoint) => {
      if (!groups[endpoint.tag]) {
        groups[endpoint.tag] = [];
      }
      groups[endpoint.tag].push(endpoint);
    });
    return groups;
  }, [endpoints, search]);

  const toggleTag = (tag: string) => {
    setExpandedTags((prev) => ({
      ...prev,
      [tag]: prev[tag] === undefined ? false : !prev[tag],
    }));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-80 flex-shrink-0 border-r border-zinc-200 bg-white flex flex-col h-full overflow-hidden transition-transform duration-300 ease-in-out lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors lg:hidden"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-5 border-b border-zinc-200">
          <button
            onClick={handleSelectOverview}
            className="w-full text-left group pr-8 lg:pr-0"
          >
            <span className="text-[10px] font-semibold text-zinc-400 tracking-widest block mb-1">
              API SPECIFICATION
            </span>
            <h2 className="font-bold text-zinc-900 truncate tracking-tight text-lg">
              {spec.info.title}
            </h2>
            <p className="text-xs text-zinc-500 tracking-wider mt-1">
              v{spec.info.version}
            </p>
          </button>

          {/* Search */}
          <div className="relative mt-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search endpoints..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-zinc-200 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 transition-colors"
            />
          </div>
        </div>

        {/* Endpoints List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {Object.entries(groupedEndpoints).map(
            ([tag, eps]: [string, ParsedEndpoint[]]) => {
              const isExpanded = expandedTags[tag] !== false; // default true
              return (
                <div key={tag} className="mb-4">
                  <button
                    onClick={() => toggleTag(tag)}
                    className="flex items-center justify-between w-full px-1 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 group transition-colors mb-1"
                  >
                    <span>{tag}</span>
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-600" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-600" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="space-y-0.5">
                      {eps.map((endpoint) => {
                        const isSelected = selectedEndpointId === endpoint.id;
                        return (
                          <button
                            key={endpoint.id}
                            onClick={() => handleSelectEndpoint(endpoint.id)}
                            className={cn(
                              "w-full flex items-start gap-3 px-2 py-2 text-left transition-all duration-200 border",
                              isSelected
                                ? "bg-zinc-50 border-zinc-900 text-zinc-900"
                                : "border-transparent hover:border-zinc-200 text-zinc-600 hover:text-zinc-900",
                            )}
                          >
                            <span
                              className={cn(
                                "text-[10px] font-bold px-1.5 py-0.5 border uppercase w-12 text-center shrink-0 mt-0.5",
                                METHOD_COLORS[endpoint.method] ||
                                  "text-zinc-600 bg-zinc-50 border-zinc-200",
                              )}
                            >
                              {endpoint.method}
                            </span>
                            <div className="overflow-hidden min-w-0">
                              <div
                                className={cn(
                                  "text-sm truncate transition-colors",
                                  isSelected ? "font-semibold" : "font-medium",
                                )}
                              >
                                {endpoint.operation.summary || endpoint.path}
                              </div>
                              {endpoint.operation.summary && (
                                <div className="text-[11px] truncate opacity-60 font-mono mt-0.5">
                                  {endpoint.path}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            },
          )}
          {Object.keys(groupedEndpoints).length === 0 && (
            <div className="text-center text-zinc-400 text-sm py-12">
              No endpoints found.
            </div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-zinc-200 bg-white">
          <div className="space-y-2 mb-3">
            {hasSecuritySchemes && (
              <button
                onClick={onAuthorize}
                className={cn(
                  "w-full flex items-center justify-center gap-2 px-4 py-2.5 border text-xs font-medium tracking-wider transition-all",
                  Object.keys(credentials).filter((k) => credentials[k]).length > 0
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "border-zinc-200 text-zinc-700 hover:border-zinc-900"
                )}
              >
                <Shield className="w-4 h-4" />
                {Object.keys(credentials).filter((k) => credentials[k]).length > 0
                  ? "AUTHORIZED"
                  : "AUTHORIZE"}
                {Object.keys(credentials).filter((k) => credentials[k]).length > 0 && (
                  <Lock className="w-3 h-3 ml-0.5" />
                )}
              </button>
            )}

          {specId && (
            <button
              onClick={onUploadNew}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-200 text-xs font-medium tracking-wider text-zinc-500 hover:text-zinc-900 hover:border-zinc-900 transition-all"
            >
              <Upload className="w-4 h-4" />
              UPLOAD NEW SPEC
            </button>
          )}
          {!specId && onUploadNew && (
            <button
              onClick={onUploadNew}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-200 text-xs font-medium tracking-wider text-zinc-500 hover:text-zinc-900 hover:border-zinc-900 transition-all"
            >
              <Upload className="w-4 h-4" />
              SIGN IN TO UPLOAD
            </button>
          )}
          </div>
        </div>
      </div>
    </>
  );
}
